import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Truck, BadgePercent, CalendarDays, MapPin, ShieldCheck, RefreshCcw, Loader2, CheckCircle2 } from "lucide-react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { ShinyButton } from "@/components/ui/ShinyButton";
import { usePrimePurchase } from "@/lib/usePrimePurchase";

export default function PrimePage() {
  const { user } = useAuth();
  const { prime, primeActive } = useStore();
  const { buy, buying } = usePrimePurchase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // after a successful purchase, return the buyer to where they came from (PDP in PRIME mode)
  const returnTo = (() => {
    const r = searchParams.get("return");
    return r && r.startsWith("/") && !r.startsWith("//") ? r : null;
  })();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/prime/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const m = prime?.membership;

  return (
    <div className="pb-20 pt-28" data-testid="prime-page">
      {/* hero — PRIME charcoal band with champagne detailing */}
      <div className="relative overflow-hidden bg-[#242521] px-4 pb-16 pt-14 sm:px-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(228,182,109,0.14),transparent_55%)]" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E4B66D]/40 bg-[#E4B66D]/10 px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#E4B66D]">
            <Crown className="h-3.5 w-3.5" /> Membership
          </span>
          <h1 className="font-display mt-5 text-5xl font-semibold tracking-tight text-[#F8F6F0] sm:text-6xl">
            PAQVERO <span className="text-[#E4B66D]">PRIME</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#C9C7BC] sm:text-base">
            One simple membership for businesses that order often: savings on every eligible delivery and member-only prices on selected products.
          </p>
          <button
            onClick={() => navigate("/prime-store")}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-[#E4B66D]/40 px-4 py-1.5 text-xs font-bold text-[#E4B66D] transition-colors hover:bg-[#E4B66D]/10"
            data-testid="prime-goto-store"
          >
            Browse the PRIME Store →
          </button>
        </motion.div>
      </div>

      {/* purchase / status box — floats over the charcoal band edge */}
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
        className="relative mx-auto -mt-10 max-w-lg overflow-hidden rounded-3xl border border-[#E4B66D]/40 bg-[#242521] p-8 text-center shadow-[0_24px_60px_-16px_rgba(36,37,33,0.55),0_0_40px_-12px_rgba(228,182,109,0.35)]"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E4B66D]/70 to-transparent" />
        {cfg && (
          <>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#C9C7BC]">One-time payment</p>
            <p className="font-display mt-2 text-5xl font-semibold text-[#E4B66D]" data-testid="prime-price">{fmt(cfg.price)}</p>
            <p className="mt-1 text-xs text-[#A5A49A]">{cfg.validity_days} days · no auto-renewal</p>
          </>
        )}

        {primeActive && m ? (
          <div className="mt-6" data-testid="prime-active-box">
            <p className="flex items-center justify-center gap-2 text-sm font-bold text-emerald-400">
              <CheckCircle2 className="h-4 w-4" /> PRIME is active
            </p>
            <p className="mt-2 text-xs text-[#C9C7BC]">
              Valid till {new Date(m.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              {" · "}{fmt(m.remaining)} of {fmt(m.cap_total)} delivery savings left
            </p>
            <button onClick={() => navigate("/account/prime")} className="mt-5 text-xs font-bold text-[#E4B66D] hover:text-[#EFC582]" data-testid="prime-goto-account">
              View membership details →
            </button>
          </div>
        ) : !user ? (
          <ShinyButton
            variant="primeLight"
            data-testid="prime-signin-btn"
            onClick={() => navigate("/login", { state: { from: "/prime" } })}
            className="mt-6"
            contentClassName="px-8 py-3.5 text-sm font-bold"
          >
            Sign in to get PRIME
          </ShinyButton>
        ) : (
          <ShinyButton
            variant="primeLight"
            data-testid="prime-buy-btn"
            onClick={async () => {
              const ok = await buy();
              if (ok && returnTo) navigate(returnTo, { replace: true });
            }}
            disabled={buying}
            className="mt-6"
            contentClassName="px-8 py-3.5 text-sm font-bold"
          >
            {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
            {prime?.state === "expired" ? "Renew PRIME" : "Get PRIME now"}
          </ShinyButton>
        )}
        <p className="mt-4 text-[11px] text-[#A5A49A]">Secure online payment via Razorpay · UPI, cards, net banking</p>
      </motion.div>

      {/* rules grid */}
      {cfg && (
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="prime-rules">
          {[
            { icon: Truck, title: `${fmt(cfg.delivery_discount)} off delivery`, body: `On every eligible order — up to ${cfg.max_deliveries} deliveries per membership.` },
            { icon: BadgePercent, title: `${fmt(cfg.total_cap)} total savings cap`, body: "Your delivery savings add up to this cap each membership period." },
            { icon: CalendarDays, title: `${cfg.validity_days}-day validity`, body: "Starts the moment your payment is verified. No auto-renewal — you stay in control." },
            { icon: ShieldCheck, title: `Min. order ${fmt(cfg.min_order)}`, body: "The delivery benefit applies when your merchandise total (before delivery) reaches this." },
            { icon: MapPin, title: `Within ${cfg.radius_km} km of our outlet`, body: `Served from ${cfg.outlet_label}. Outside the delivery zone, standard charges apply.` },
            { icon: RefreshCcw, title: "Renew anytime", body: "Expired? Renew with a fresh payment — a new validity window and allowance start instantly." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-line bg-surface p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#E4B66D]/50 hover:shadow-card">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#242521]">
                <Icon className="h-4 w-4 text-[#E4B66D]" />
              </span>
              <p className="mt-3 text-sm font-bold text-1">{title}</p>
              <p className="mt-1 text-xs leading-relaxed text-2">{body}</p>
            </div>
          ))}
        </div>
      )}

      <p className="mx-auto mt-10 max-w-xl text-center text-[11px] leading-relaxed text-3" data-testid="prime-fineprint">
        Coupons can't be combined with a PRIME delivery benefit. You always pay any delivery amount left after the discount.
        Member prices apply automatically on selected products while your membership is active.
      </p>
      </div>
    </div>
  );
}
